/**
 * The FAQs for `/tools/video-checker`, in their own module.
 *
 * A route's `head` is never code-split, so importing this list from the tool
 * component would pull the whole component into the shared entry chunk that
 * every visitor to every page downloads — the trap `marketing-perf-ceiling`
 * documents, and one the Mastodon limit checker was caught by.
 *
 * Rendered visibly on the page AND emitted as `FAQPage` JSON-LD from the route.
 */
export const VIDEO_CHECKER_FAQS = [
  {
    q: "What video format works on every social network?",
    a: "H.264 video in an MP4 container, at 1080p or below. It is the one combination every network in this list documents as supported, and the safe answer whenever you only want to export once. Everything else — HEVC, AV1, VP8, VP9 — is accepted somewhere and warned about somewhere else.",
  },
  {
    q: "Does Bluesky support HEVC?",
    a: "Yes. We published an HEVC video to Bluesky and it went through, as did AV1. Bluesky transcodes uploads via video.bsky.app and accepts MP4, MPEG, WebM and MOV, so its codec list in our own publish path is empty — not because we assume it is permissive, but because we tested it.",
  },
  {
    q: "What is the video size limit for X, LinkedIn, Bluesky and Mastodon?",
    a: "X caps video at 100 MB and Bluesky at the same; Mastodon's default is 99 MB, though a server can change it. LinkedIn allows 500 MB, and Pinterest and YouTube 2 GB. These are the caps our publisher enforces before it uploads, so a file over one of them fails here rather than halfway through a transfer.",
  },
  {
    q: "Is my video uploaded anywhere?",
    a: "No. Two 512 KB slices are read from the file in your own browser to identify the container and codec, and a hidden video element reads the duration and resolution. Nothing is sent to us or to anyone else — a 2 GB file costs about a megabyte of reading and never leaves your device.",
  },
  {
    q: "Why does it say “may not publish” instead of “will fail”?",
    a: "Because documentation and behaviour disagree, and we would rather be accurate than confident. Several platforms accept codecs their own specs do not list: AV1 published fine to Facebook, Instagram and Threads despite their docs naming only H.264 and HEVC. A warning here means the network does not document that codec as supported — not that we watched it fail.",
  },
] as const;
